import { useState, memo } from "react";

/**
 * Royal Lady — <LuxImage>
 * Premium responsive image primitive for the storefront.
 *
 * Features:
 *  - Shimmer skeleton until the image decodes.
 *  - Auto-generates responsive `srcSet` + `sizes` for Unsplash + Emergent-CDN URLs.
 *  - Serves WebP where the URL supports it, with a JPEG fallback (via <picture>).
 *  - Consistent white background beneath every product photo (fashion-standard).
 *  - Graceful branded SVG fallback if the source 404s.
 *  - Works with any Tailwind aspect wrapper — the child <img> is absolutely
 *    positioned so the parent controls the aspect ratio and layout.
 *
 * Contract:
 *  - `src`   original URL (required)
 *  - `alt`   accessible description
 *  - `sizes` CSS `sizes` string, e.g. "(min-width: 768px) 25vw, 50vw"
 *  - `imgClassName` classes passed to the inner <img> (e.g. hover scale, object-fit)
 *  - `priority` when true, image is eager-loaded (used for hero / first fold)
 *  - `objectFit` "cover" (default) or "contain"
 *  - `showSkeleton` show shimmer while loading (default true)
 *  - `testId` optional data-testid for the wrapper
 */

const RESPONSIVE_WIDTHS = [320, 480, 640, 800, 1024, 1280, 1600, 2000];

const isUnsplash = (url) => /images\.unsplash\.com/.test(url || "");
const isEmergentCdn = (url) => /customer-assets\.emergentagent(?:cdn)?\.(?:net|com)/.test(url || "");

/** Strip any existing width / format params so we can inject our own. */
const stripSizingParams = (u) => {
  try {
    const url = new URL(u);
    // Unsplash + Emergent CDN both use `w`, `fm`, `q`
    url.searchParams.delete("w");
    url.searchParams.delete("fm");
    url.searchParams.delete("q");
    // keep everything else (auth params, crop, cs, etc.)
    return url;
  } catch {
    return null;
  }
};

const buildVariant = (baseUrl, width, format) => {
  const url = stripSizingParams(baseUrl);
  if (!url) return null;
  url.searchParams.set("w", String(width));
  url.searchParams.set("q", "82");
  if (format) url.searchParams.set("fm", format);
  return url.toString();
};

const buildSrcSet = (src, format) => {
  if (!src) return "";
  const variants = [];
  for (const w of RESPONSIVE_WIDTHS) {
    const u = buildVariant(src, w, format);
    if (u) variants.push(`${u} ${w}w`);
  }
  return variants.join(", ");
};

const FALLBACK_SVG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 400'>
      <rect width='300' height='400' fill='#FAFAFA'/>
      <text x='50%' y='47%' font-family='Georgia,serif' font-size='24' fill='#D4AF37' text-anchor='middle' dominant-baseline='middle'>RL</text>
      <text x='50%' y='58%' font-family='Georgia,serif' font-size='11' letter-spacing='2' fill='#8b6c15' text-anchor='middle' dominant-baseline='middle'>ROYAL LADY</text>
    </svg>`
  );

const LuxImage = ({
  src,
  alt = "",
  sizes = "(min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw",
  imgClassName = "",
  priority = false,
  objectFit = "cover",
  showSkeleton = true,
  onLoad,
  testId,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  const optimisable = !errored && (isUnsplash(src) || isEmergentCdn(src));

  const webpSrcSet = optimisable ? buildSrcSet(src, "webp") : "";
  const jpegSrcSet = optimisable ? buildSrcSet(src, "jpg") : "";
  // Fallback single-src used for the <img> itself (also serves non-optimisable URLs)
  const fallbackSrc = errored
    ? FALLBACK_SVG
    : optimisable
    ? buildVariant(src, 800, "jpg") || src
    : src || FALLBACK_SVG;

  const handleLoad = (e) => {
    setLoaded(true);
    onLoad?.(e);
  };

  const handleError = () => {
    if (!errored) setErrored(true);
    setLoaded(true);
  };

  return (
    <div
      data-testid={testId}
      className="absolute inset-0 h-full w-full overflow-hidden bg-white"
    >
      {/* Shimmer skeleton — sits behind and fades out once decoded */}
      {showSkeleton && (
        <div
          aria-hidden="true"
          className={`absolute inset-0 transition-opacity duration-500 ${
            loaded ? "opacity-0" : "opacity-100"
          }`}
        >
          <div className="lux-shimmer h-full w-full" />
        </div>
      )}

      <picture>
        {!errored && webpSrcSet && (
          <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />
        )}
        {!errored && jpegSrcSet && (
          <source type="image/jpeg" srcSet={jpegSrcSet} sizes={sizes} />
        )}
        <img
          src={fallbackSrc}
          alt={alt}
          decoding="async"
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          onLoad={handleLoad}
          onError={handleError}
          draggable={false}
          className={`absolute inset-0 h-full w-full ${
            objectFit === "contain" ? "object-contain" : "object-cover"
          } transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"} ${imgClassName}`}
        />
      </picture>
    </div>
  );
};

export default memo(LuxImage);
